<div class="container py-3 ppk-studio">
  <div class="d-flex align-items-center mb-3">
    <h2 class="m-0">{{ title }}</h2>
    <button class="btn btn-link btn-sm p-0 text-primary ms-2"
            (click)="openHelp()"
            matTooltip="How to use PPK Studio">
      <span class="material-icons" aria-hidden="true">menu_book</span>
    </button>
  </div>

  <div class="row g-3">
    <!-- Left column: controls -->
    <div class="col-12 col-lg-4">
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="m-0 fw-bold">PPK processing</h4>
            <div class="d-flex align-items-center gap-2">
              <button class="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
                      (click)="toggleQuickStart()"
                      [attr.aria-expanded]="quickStartExpanded">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1 text-warning" style="font-size: 18px;" aria-hidden="true">menu_book</span>
                  <span class="me-1">Quick Guide</span>
                  <span class="material-icons" style="font-size: 20px;">
                    {{ quickStartExpanded ? 'expand_less' : 'expand_more' }}
                  </span>
                </span>
              </button>
            </div>
          </div>

          <!-- Quick Start -->
          <div class="quick-start mb-3" *ngIf="quickStartExpanded">
            <div class="quick-start-title mb-2">Steps:</div>
            <div class="quick-start-step">
              <div class="step-left">
                <span class="step-num">1</span>
                <span class="step-text">Upload Rover Data (.ubx/.dat/.obs)</span>
              </div>
              <span class="badge"
                    [ngClass]="{ 'bg-success': stepRoverStatus.tone === 'success', 'bg-info': stepRoverStatus.tone === 'info', 'bg-secondary': stepRoverStatus.tone === 'muted', 'bg-danger': stepRoverStatus.tone === 'danger' }">
                {{ stepRoverStatus.label }}
              </span>
            </div>
            <div class="quick-start-step">
              <div class="step-left">
                <span class="step-num">2</span>
                <span class="step-text">Upload Base Data (.rtcm3/.dat/.obs)</span>
              </div>
              <span class="badge"
                    [ngClass]="{ 'bg-success': stepBaseStatus.tone === 'success', 'bg-info': stepBaseStatus.tone === 'info', 'bg-secondary': stepBaseStatus.tone === 'muted', 'bg-danger': stepBaseStatus.tone === 'danger' }">
                {{ stepBaseStatus.label }}
              </span>
            </div>
            <div class="quick-start-step">
              <div class="step-left">
                <span class="step-num">3</span>
                <span class="step-text"
                      matTooltip="NAV is required only when Rover input is RINEX OBS. For UBX/DAT, rover.nav is auto-generated.">
                  Upload Navigation (.nav)
                </span>
              </div>
              <span class="badge"
                    [ngClass]="{ 'bg-success': stepNavStatus.tone === 'success', 'bg-info': stepNavStatus.tone === 'info', 'bg-secondary': stepNavStatus.tone === 'muted', 'bg-danger': stepNavStatus.tone === 'danger' }">
                {{ stepNavStatus.label }}
              </span>
            </div>
            <div class="quick-start-step">
              <div class="step-left">
                <span class="step-num">4</span>
                <span class="step-text">Process → result.pos</span>
              </div>
              <span class="badge"
                    [ngClass]="{ 'bg-success': stepProcessStatus.tone === 'success', 'bg-info': stepProcessStatus.tone === 'info', 'bg-secondary': stepProcessStatus.tone === 'muted', 'bg-danger': stepProcessStatus.tone === 'danger' }">
                {{ stepProcessStatus.label }}
              </span>
            </div>
            <div class="quick-start-step">
              <div class="step-left">
                <span class="step-num">5</span>
                <span class="step-text">Export (optional)</span>
              </div>
              <span class="badge"
                    [ngClass]="{ 'bg-success': stepExportStatus.tone === 'success', 'bg-info': stepExportStatus.tone === 'info', 'bg-secondary': stepExportStatus.tone === 'muted', 'bg-danger': stepExportStatus.tone === 'danger' }">
                {{ stepExportStatus.label }}
              </span>
            </div>
            <div class="quick-start-advice mt-2" *ngIf="guidanceText">
              <span class="material-icons advice-icon" aria-hidden="true">assistant</span>
              <div class="advice-body">
                <div class="advice-label">Next Step Advisor</div>
                <div class="advice-text">
                  <ng-container *ngIf="!allStepsCompleted; else doneTpl">
                    <span class="me-2">Next step:</span>
                    <span class="badge bg-primary me-2">Step {{ nextStepNumber }}</span>
                    <span>{{ nextStepShortText }}</span>
                  </ng-container>
                  <ng-template #doneTpl>
                    <span class="me-2">Status:</span>
                    <span class="badge bg-success me-2">All done</span>
                    <span>{{ nextStepShortText }}</span>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>

          <!-- Rover -->
          <div class="card mb-3 border-secondary" style="background-color: #f8f9fa;">
            <div class="card-header py-2 border-bottom border-secondary" style="background-color: #e9ecef;">
              <span class="fw-bold text-muted small">Rover</span>
              <button class="btn btn-link btn-sm p-0 ms-2 text-muted"
                      matTooltip="Upload rover UBX/DAT or RINEX OBS. UBX/DAT will be converted to rover.obs/rover.nav automatically.">
                <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
              </button>
            </div>
            <div class="card-body p-2">
              <ng-container *ngIf="files.roverObsName; else roverUploadTpl">
                <div class="ppk-file-pill d-flex align-items-center justify-content-between p-2 border rounded bg-white">
                  <a [href]="files.roverObsUrl" class="d-flex align-items-center text-decoration-none text-dark text-truncate" target="_blank" rel="noopener">
                    <span class="material-icons me-2 text-primary">insert_drive_file</span>
                    <span class="text-truncate">{{ files.roverObsName }}</span>
                  </a>
                  <button class="btn btn-link btn-sm p-0 text-danger ms-2" (click)="onDeleteRover()" [disabled]="roverLoading || isDemo" matTooltip="Delete file">
                    <span class="material-icons" style="font-size: 1.2rem;">delete_outline</span>
                  </button>
                </div>
              </ng-container>
              <ng-template #roverUploadTpl>
                <button class="btn btn-outline-primary w-100 text-start"
                        (click)="onUploadRover()"
                        [disabled]="roverLoading || isDemo">
                  <span class="d-flex align-items-center">
                    <span class="material-icons me-2">file_upload</span>
                    <span class="flex-grow-1">
                      <span class="d-block">Upload Rover Site Data</span>
                      <span class="d-block small text-muted">.ubx / .dat / .obs</span>
                    </span>
                    <span *ngIf="roverLoading" class="spinner-border spinner-border-sm ms-2"></span>
                  </span>
                </button>
                <div *ngIf="roverProgress !== null" class="progress mt-2">
                  <div class="progress-bar" role="progressbar"
                       [style.width.%]="(roverProgress || 0) * 100">
                    {{ (roverProgress || 0) * 100 | number:'1.0-0' }}%
                  </div>
                </div>
              </ng-template>
            </div>
          </div>

          <!-- Base -->
          <div class="card mb-3 border-secondary" style="background-color: #f8f9fa;">
            <div class="card-header py-2 border-bottom border-secondary" style="background-color: #e9ecef;">
              <span class="fw-bold text-muted small">Base</span>
              <button class="btn btn-link btn-sm p-0 ms-2 text-muted"
                      matTooltip="Upload base RTCM3/DAT or RINEX OBS. RTCM3/DAT will be converted to base.obs automatically.">
                <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
              </button>
            </div>
            <div class="card-body p-2">
              <ng-container *ngIf="files.baseObsName; else baseUploadTpl">
                <div class="ppk-file-pill d-flex align-items-center justify-content-between p-2 border rounded bg-white">
                  <a [href]="files.baseObsUrl" class="d-flex align-items-center text-decoration-none text-dark text-truncate" target="_blank" rel="noopener">
                    <span class="material-icons me-2 text-primary">insert_drive_file</span>
                    <span class="text-truncate">{{ files.baseObsName }}</span>
                  </a>
                  <button class="btn btn-link btn-sm p-0 text-danger ms-2" (click)="onDeleteBase()" [disabled]="baseLoading || isDemo" matTooltip="Delete file">
                    <span class="material-icons" style="font-size: 1.2rem;">delete_outline</span>
                  </button>
                </div>
              </ng-container>
              <ng-template #baseUploadTpl>
                <button class="btn btn-outline-primary w-100 text-start mb-2"
                        (click)="onUploadBase()"
                        [disabled]="baseLoading || isDemo">
                  <span class="d-flex align-items-center">
                    <span class="material-icons me-2">file_upload</span>
                    <span class="flex-grow-1">
                      <span class="d-block">Upload Base Site Data</span>
                      <span class="d-block small text-muted">.rtcm3 / .dat / .obs</span>
                    </span>
                    <span *ngIf="baseLoading" class="spinner-border spinner-border-sm ms-2"></span>
                  </span>
                </button>

                <div class="text-center text-muted small my-2">- OR -</div>

                <button class="btn btn-outline-primary w-100 text-start"
                        (click)="onSelectDownloaderFile()"
                        [disabled]="baseLoading || isDemo">
                  <span class="d-flex align-items-center">
                    <span class="material-icons me-2">cloud_download</span>
                    <span class="flex-grow-1">
                      <span class="d-block">Select Existing Data from PPK downloader</span>
                      <span class="d-block small text-muted">Select from history</span>
                    </span>
                  </span>
                </button>

                <div *ngIf="baseProgress !== null" class="progress mt-2">
                  <div class="progress-bar" role="progressbar"
                       [style.width.%]="(baseProgress || 0) * 100">
                    {{ (baseProgress || 0) * 100 | number:'1.0-0' }}%
                  </div>
                </div>
              </ng-template>
            </div>
          </div>

          <!-- Navigation -->
          <div class="card mb-3 border-secondary" style="background-color: #f8f9fa;">
            <div class="card-header py-2 border-bottom border-secondary" style="background-color: #e9ecef;">
              <span class="fw-bold text-muted small">Navigation</span>
              <button class="btn btn-link btn-sm p-0 ms-2 text-muted"
                      matTooltip="Upload rover.nav only if Rover input is RINEX OBS. For UBX/DAT, rover.nav is auto-generated.">
                <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
              </button>
            </div>
            <div class="card-body p-2">
              <ng-container *ngIf="files.roverNavName; else navUploadTpl">
                <div class="ppk-file-pill d-flex align-items-center justify-content-between p-2 border rounded bg-white">
                  <a [href]="files.roverNavUrl" class="d-flex align-items-center text-decoration-none text-dark text-truncate" target="_blank" rel="noopener">
                    <span class="material-icons me-2 text-primary">insert_drive_file</span>
                    <span class="text-truncate">{{ files.roverNavName }}</span>
                  </a>
                  <button class="btn btn-link btn-sm p-0 text-danger ms-2" (click)="onDeleteNav()" [disabled]="navLoading || isDemo" matTooltip="Delete file">
                    <span class="material-icons" style="font-size: 1.2rem;">delete_outline</span>
                  </button>
                </div>
              </ng-container>
              <ng-template #navUploadTpl>
                <button class="btn btn-outline-primary w-100 text-start"
                        (click)="onUploadNavigation()"
                        [disabled]="navLoading || isDemo">
                  <span class="d-flex align-items-center">
                    <span class="material-icons me-2">file_upload</span>
                    <span class="flex-grow-1">
                      <span class="d-block">Upload Navigation Data</span>
                      <span class="d-block small text-muted">.nav</span>
                    </span>
                    <span *ngIf="navLoading" class="spinner-border spinner-border-sm ms-2"></span>
                  </span>
                </button>
                <div *ngIf="navProgress !== null" class="progress mt-2">
                  <div class="progress-bar" role="progressbar"
                       [style.width.%]="(navProgress || 0) * 100">
                    {{ (navProgress || 0) * 100 | number:'1.0-0' }}%
                  </div>
                </div>
              </ng-template>
            </div>
          </div>

          <!-- Process button -->
          <div class="d-flex align-items-center">
            <button class="btn btn-primary me-2"
                    (click)="onProcess()"
                    [disabled]="!canProcess || processLoading">
              <span class="d-flex align-items-center">
                <span class="material-icons me-1">play_arrow</span>
                <span>Process</span>
                <span *ngIf="processLoading" class="spinner-border spinner-border-sm ms-2"></span>
              </span>
            </button>
            <button class="btn btn-link btn-sm p-0 text-secondary ms-auto text-decoration-none" (click)="onRefreshStatus()" matTooltip="Refresh status">
              <span class="d-flex align-items-center">
                <span class="material-icons me-1" style="font-size: 1.2rem;">refresh</span>
                <span>Refresh</span>
              </span>
            </button>
          </div>

          <!-- Status Row -->
          <div class="mt-3" *ngIf="statusText">
            <small class="text-primary fw-bold">{{ statusText }}</small>
          </div>

          <!-- Spacer between PPK status and Export section -->
          <div class="my-3"></div>

          <!-- Result File (if exists) -->
          <div class="card mb-3 border-secondary" style="background-color: #f8f9fa;" *ngIf="files.resultPosUrl">
            <div class="card-header py-2 border-bottom border-secondary" style="background-color: #e9ecef;">
              <span class="fw-bold text-muted small">PPK Result</span>
              <button class="btn btn-link btn-sm p-0 ms-2 text-muted"
                      matTooltip="This is the output of the backend PPK processing. Click the file name to download result.pos.">
                <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
              </button>
            </div>
            <div class="card-body p-2">
              <div class="ppk-file-pill d-flex align-items-center justify-content-between p-2 border rounded bg-white">
                <a [href]="files.resultPosUrl" class="d-flex align-items-center text-decoration-none text-dark text-truncate" target="_blank" rel="noopener">
                  <span class="material-icons me-2 text-success">check_circle</span>
                  <span class="text-truncate">result.pos</span>
                </a>
                <button class="btn btn-link btn-sm p-0 text-danger ms-2" (click)="onDeleteResult()" [disabled]="isDemo" matTooltip="Delete result file">
                  <span class="material-icons" style="font-size: 1.2rem;">delete_outline</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Export To -->
          <div class="d-flex align-items-center mb-2">
            <button class="btn btn-success me-2"
                    (click)="onExportTo()"
                    [disabled]="!canExport">
              <span class="d-flex align-items-center">
                <span class="material-icons me-1">download</span>
                <span>Export To</span>
                <span *ngIf="exportLoading" class="spinner-border spinner-border-sm ms-2"></span>
              </span>
            </button>
            <small class="text-muted">CSV, KML, KMZ, GPX, or SHP formats.</small>
          </div>

          <div *ngIf="exportStatusText" class="mb-2">
            <small class="text-primary fw-bold">{{ exportStatusText }}</small>
          </div>

          <!-- Exported files -->
          <div class="card mb-3 border-secondary" style="background-color: #f8f9fa;">
            <div class="card-header py-2 border-bottom border-secondary d-flex align-items-center justify-content-between" style="background-color: #e9ecef;">
              <div class="d-flex align-items-center">
                <span class="fw-bold text-muted small">Exported files</span>
                <button class="btn btn-link btn-sm p-0 ms-2 text-muted"
                        matTooltip="Exported from result.pos. Click any file name to download. Use the trash icon to delete.">
                  <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                </button>
              </div>
              <button class="btn btn-link btn-sm p-0 text-danger text-decoration-none"
                      (click)="onDeleteAllExportFiles()"
                      [disabled]="isDemo || exportedFiles.length === 0"
                      matTooltip="Delete all exported files">
                <span class="d-flex align-items-center">
                  <span class="material-icons me-1" style="font-size: 1.1rem;">delete_sweep</span>
                  <span>Delete all</span>
                </span>
              </button>
            </div>
            <div class="card-body p-2">
              <ng-container *ngIf="exportedFiles.length > 0; else noExportedFilesTpl">
                <div class="list-group list-group-flush">
                  <div class="list-group-item d-flex align-items-center justify-content-between py-2 px-2" *ngFor="let f of exportedFiles">
                    <a [href]="f.url"
                       class="text-decoration-none text-dark text-truncate"
                       target="_blank"
                       rel="noopener">
                      <span class="material-icons me-2 text-primary" style="font-size: 1.1rem;">insert_drive_file</span>
                      <span class="text-truncate">{{ f.name }}</span>
                    </a>
                    <button class="btn btn-link btn-sm p-0 text-danger ms-2"
                            (click)="onDeleteExportFile(f.name)"
                            [disabled]="isDemo"
                            matTooltip="Delete file">
                      <span class="material-icons" style="font-size: 1.2rem;">delete_outline</span>
                    </button>
                  </div>
                </div>
              </ng-container>
              <ng-template #noExportedFilesTpl>
                <div class="text-muted small px-2 py-2">No exported files.</div>
              </ng-template>
            </div>
          </div>

          <div *ngIf="isDemo" class="alert alert-info mt-3 py-2 small mb-0">
            You have read-only access in demo mode. Uploads and processing are disabled.
          </div>
        </div>
      </div>
    </div>

    <!-- Right column: Plot area -->
    <div class="col-12 col-lg-8">
      <div class="card shadow-sm h-100">
        <div class="card-header">
          <ul class="nav nav-tabs card-header-tabs">
            <li class="nav-item">
              <span class="nav-link active">PPK Result</span>
            </li>
          </ul>
        </div>
        <div class="card-body d-flex flex-column">
          <!--
          <div class="mb-3">
            <h5 class="card-title">PPK Result</h5>
            <ng-container *ngIf="files.resultPosUrl; else noResultTpl">
              <p class="mb-1">
                Latest <code>result.pos</code> file:
              </p>
              <a [href]="files.resultPosUrl" target="_blank" rel="noopener" class="small">
                {{ files.resultPosUrl }}
              </a>
            </ng-container>
            <ng-template #noResultTpl>
              <p class="text-muted small mb-0">
                No <code>result.pos</code> file available yet. Once PPK processing completes, the file URL will appear here.
              </p>
            </ng-template>
          </div>
          -->

          <div class="flex-grow-1 border rounded bg-light d-flex flex-column mb-3" style="min-height: 500px; position: relative;">
            <ng-container *ngIf="files.resultPosUrl; else plotPlaceholder">
              <div class="plot-tip px-2 pt-2 text-muted small">
                Tip: Double-click a point to open Google Maps satellite view.
              </div>
              <app-ppk-plot [fileUrl]="files.resultPosUrl" style="flex: 1;"></app-ppk-plot>
            </ng-container>
            <ng-template #plotPlaceholder>
              <div class="d-flex w-100 h-100 align-items-center justify-content-center">
                <div class="text-center px-4">
                  <div class="fw-semibold mb-1">No PPK result yet</div>
                  <div class="text-muted small mb-2">
                    Upload Rover + Base (and NAV if needed), then click <strong>Process</strong>.
                  </div>
                  <div class="text-muted small">
                    Processing runs in the backend. You can leave this page and come back later.
                  </div>
                </div>
              </div>
            </ng-template>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


